import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import FullBlockingModal from '../../common/FullBlockingModal';
import { Button } from '../../common/Button';
import { ServiceCategory } from '../../../types/service';
import { useAdminStore } from '../../../stores/adminStore';
import GenericInformationModal from '../../common/GenericInformationModal';
import { resolveServiceCategoryLabel } from '../../../utils/serviceCategoryLabel';
import { getDisplayErrorMessage } from '../../../utils/errors';

interface Props {
  serviceCategory: ServiceCategory | null;
  onClose: () => void;
  onEdit: (serviceCategory: ServiceCategory) => void;
}

const getServiceCategoryDescription = (serviceCategory: ServiceCategory) =>
  serviceCategory.description ?? serviceCategory.service_description ?? '';

const getInitialLabelFr = (serviceCategory: ServiceCategory) =>
  serviceCategory.label_fr?.trim() || serviceCategory.label?.trim() || '';

const getInitialLabelEn = (serviceCategory: ServiceCategory) =>
  serviceCategory.label_en?.trim() || '';

const ServiceCategoryModalInfo: React.FC<Props> = ({ serviceCategory, onClose, onEdit }) => {
  const { t, i18n } = useTranslation(['common', 'admin', 'notifications']);
  const { approveSuggestedCategory, deleteServiceCategory } = useAdminStore();

  const [labelFr, setLabelFr] = useState(() =>
    serviceCategory ? getInitialLabelFr(serviceCategory) : ''
  );
  const [labelEn, setLabelEn] = useState(() =>
    serviceCategory ? getInitialLabelEn(serviceCategory) : ''
  );
  const [validateErrorMessage, setValidateErrorMessage] = useState<string | null>(null);
  const [deleteErrorMessage, setDeleteErrorMessage] = useState<string | null>(null);
  const [validateSuccess, setValidateSuccess] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [approving, setApproving] = useState(false);

  if (!serviceCategory) return null;

  const handleValidate = async () => {
    setApproving(true);
    setValidateErrorMessage(null);
    try {
      await approveSuggestedCategory(serviceCategory.id, {
        labelFr: labelFr.trim(),
        labelEn: labelEn.trim() || null,
      });
      setValidateSuccess(true);
    } catch (error) {
      console.error(error);
      setValidateErrorMessage(
        getDisplayErrorMessage(
          error,
          t('serviceCategoryApprovalErrorFallback', {
            ns: 'notifications',
            defaultValue: 'Could not approve service category.',
          })
        )
      );
    } finally {
      setApproving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteServiceCategory(serviceCategory.id);
      setDeleteSuccess(true);
    } catch (error) {
      console.error(error);
      setDeleteErrorMessage(
        getDisplayErrorMessage(
          error,
          t('serviceCategoryDeleteErrorFallback', {
            ns: 'admin',
            defaultValue: 'Could not delete service category.',
          })
        )
      );
    }
  };

  const displayLabel = resolveServiceCategoryLabel(serviceCategory, i18n.language, t);

  return (
    <>
      <FullBlockingModal innerClassName="md:max-w-[40rem]">
        <h2 className="text-2xl font-bold text-secondary mb-8">
          {t('service_category_details', { ns: 'admin', defaultValue: 'Service category Details' })}
        </h2>

        <div className="bg-gray-50 rounded-xl p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6 items-start">
            <div>
              <p className="text-sm font-medium text-gray-500 mb-1">
                {t('table_fields.label', { ns: 'admin' })}
              </p>
              <p className="text-lg font-semibold text-gray-900">{displayLabel}</p>
            </div>

            {serviceCategory.label_fr && (
              <div>
                <p className="text-sm font-medium text-gray-500 mb-1">
                  {t('table_fields.label_fr', { ns: 'admin' })}
                </p>
                <p className="text-base text-gray-900">{serviceCategory.label_fr}</p>
              </div>
            )}

            {serviceCategory.label_en && (
              <div>
                <p className="text-sm font-medium text-gray-500 mb-1">
                  {t('table_fields.label_en', { ns: 'admin' })}
                </p>
                <p className="text-base text-gray-900">{serviceCategory.label_en}</p>
              </div>
            )}

            {serviceCategory.created_at && (
              <div>
                <p className="text-sm font-medium text-gray-500 mb-1">
                  {t('table_fields.created_at', { ns: 'admin', defaultValue: 'Created At' })}
                </p>
                <p className="text-base text-gray-900">
                  {new Date(serviceCategory.created_at).toLocaleDateString()}
                </p>
              </div>
            )}

            {serviceCategory.validated !== undefined && (
              <div>
                <p className="text-sm font-medium text-gray-500 mb-1">
                  {t('table_fields.validated', { ns: 'admin', defaultValue: 'Validated' })}
                </p>
                <span
                  className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                    serviceCategory.validated
                      ? 'bg-green-100 text-green-800'
                      : 'bg-yellow-100 text-yellow-800'
                  }`}
                >
                  {serviceCategory.validated
                    ? t('form_fields.yes', { ns: 'common', defaultValue: 'Oui' })
                    : t('form_fields.no', { ns: 'common', defaultValue: 'Non' })}
                </span>
              </div>
            )}

            {serviceCategory.deleted !== undefined && (
              <div>
                <p className="text-sm font-medium text-gray-500 mb-1">
                  {t('table_fields.deleted', { ns: 'admin', defaultValue: 'Deleted' })}
                </p>
                <span
                  className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                    serviceCategory.deleted
                      ? 'bg-red-100 text-red-800'
                      : 'bg-green-100 text-green-800'
                  }`}
                >
                  {serviceCategory.deleted
                    ? t('form_fields.yes', { ns: 'common', defaultValue: 'Oui' })
                    : t('form_fields.no', { ns: 'common', defaultValue: 'Non' })}
                </span>
              </div>
            )}
          </div>

          {getServiceCategoryDescription(serviceCategory) && (
            <div className="mt-8 pt-6 border-t border-gray-200">
              <p className="text-sm font-medium text-gray-500 mb-2">
                {t('table_fields.description', { ns: 'admin' })}
              </p>
              <p className="text-base text-gray-900 leading-relaxed">
                {getServiceCategoryDescription(serviceCategory)}
              </p>
            </div>
          )}
        </div>

        {!serviceCategory.validated && (
          <div className="flex flex-col gap-3 mb-8">
            <label className="text-sm font-semibold text-gray-700">
              {t('table_fields.label_fr', { ns: 'admin' })}
            </label>
            <input
              value={labelFr}
              onChange={e => setLabelFr(e.target.value)}
              className="border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <label className="text-sm font-semibold text-gray-700">
              {t('table_fields.label_en', { ns: 'admin' })}
            </label>
            <input
              value={labelEn}
              onChange={e => setLabelEn(e.target.value)}
              className="border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <p className="text-xs text-gray-500">
              {t('service_category_label_hint', { ns: 'admin' })}
            </p>
          </div>
        )}

        <div className="flex flex-col sm:flex-row justify-end items-center gap-4">
          <Button
            variant="simple-text"
            onClick={onClose}
            className="font-bold rounded-full px-8 py-2 w-full sm:w-auto order-2 sm:order-1"
            type="button"
          >
            {t('common:form_actions.return', { ns: 'common', defaultValue: 'Return' })}
          </Button>

          <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto order-1 sm:order-2">
            <Button
              variant="transparent-with-border"
              onClick={() => onEdit(serviceCategory)}
              className="font-bold rounded-full px-8 py-2 w-full sm:w-auto"
              type="button"
            >
              {t('common:form_actions.edit', { ns: 'common', defaultValue: 'Edit' })}
            </Button>

            {!serviceCategory.validated && (
              <Button
                variant="primary"
                onClick={handleValidate}
                className="font-bold rounded-full px-8 py-2 w-full sm:w-auto"
                type="button"
                disabled={approving || !labelFr.trim()}
              >
                {approving
                  ? t('loading', { ns: 'common' })
                  : t('button.approveAndMakeVisibleForAll', { ns: 'notifications' })}
              </Button>
            )}

            <Button
              onClick={handleDelete}
              className="bg-high-contrast-error text-white font-bold rounded-full px-8 py-2 w-full sm:w-auto"
              type="button"
            >
              {t('common:form_actions.remove_serviceCategory', {
                ns: 'common',
                defaultValue: 'Supprimer le service',
              })}
            </Button>
          </div>
        </div>
      </FullBlockingModal>

      {deleteSuccess ? (
        <GenericInformationModal
          title={t('serviceCategoryDeleted', { ns: 'admin' })}
          message=""
          onClose={() => {
            setDeleteSuccess(false);
            onClose();
          }}
          closeButtonLabel={t('common:form_actions.close', { ns: 'common' })}
        />
      ) : (
        deleteErrorMessage && (
          <GenericInformationModal
            title={t('serviceCategoryDeleteError', { ns: 'admin' })}
            message=""
            onClose={() => {
              setDeleteErrorMessage(null);
            }}
            errorDetails={deleteErrorMessage}
            closeButtonLabel={t('common:form_actions.close', { ns: 'common' })}
          />
        )
      )}

      {validateSuccess ? (
        <GenericInformationModal
          title={t('serviceCategoryApproved', { ns: 'notifications' })}
          message=""
          onClose={() => {
            setValidateSuccess(false);
            onClose();
          }}
          closeButtonLabel={t('common:form_actions.close', { ns: 'common' })}
        />
      ) : (
        validateErrorMessage && (
          <GenericInformationModal
            title={t('serviceCategoryApprovalError', { ns: 'notifications' })}
            message=""
            onClose={() => {
              setValidateErrorMessage(null);
            }}
            errorDetails={validateErrorMessage}
            closeButtonLabel={t('common:form_actions.close', { ns: 'common' })}
          />
        )
      )}
    </>
  );
};

export default ServiceCategoryModalInfo;
